import { useMutation, useQueryClient } from '@tanstack/react-query'
import { isAxiosError } from 'axios'

import { useAccessToken } from '@b-stock/fe-core/bstock-next/components'
import {
  makeOptionallyAuthenticatedQuery,
  getWatchedListingsClient,
  fetchAllResults,
} from '@b-stock/fe-core/bstock-next/queries'
import { buildAxiosRequestConfig } from '@b-stock/fe-core/bstock-react'
import type {
  WatchedListingsApiCreateOrUpdateWatchedListingRequest,
  WatchedListingsApiDeleteWatchedListingRequest,
} from '@b-stock/saved-search-api-client'
import {
  GetWatchlistSortOrderEnum,
  GetWatchlistSortByEnum,
} from '@b-stock/saved-search-api-client'

import type { ListingNoteWriteOptions } from './listingNotesQuery'
import { invalidateListingNotes } from './listingNotesQuery'

export type WatchlistStatus = {
  isWatchlisted: boolean
  note?: string
}

const getWatchedStatus = async (
  accessToken: string | null,
  requestParams: { personId?: string; listingId: string }
): Promise<WatchlistStatus> => {
  if (!requestParams.personId) {
    // if person is logged out, display heart as unwatched
    return {
      isWatchlisted: false,
    }
  }

  try {
    const { data } = await getWatchedListingsClient().getWatchedListing(
      {
        personId: requestParams.personId,
        listingId: requestParams.listingId,
      },
      buildAxiosRequestConfig({ accessToken })
    )

    const note = data.note || undefined
    return {
      isWatchlisted: true,
      ...(note ? { note } : {}),
    }
  } catch (error: unknown) {
    if (isAxiosError(error) && error.response) {
      const status = error.response.status
      // if user has not saved the listing or if they are logged out, show the empty heart (unwatched)
      if (status === 404 || status === 401) {
        return {
          isWatchlisted: false,
        }
      }
    }
    throw error
  }
}

export const watchedStatusQuery = makeOptionallyAuthenticatedQuery<
  [{ personId?: string; listingId: string }],
  WatchlistStatus
>('watched-status', getWatchedStatus)

export type WatchedListingEntry = {
  listingId: string
  note?: string
}

// Get all watched listings for a person and return listing IDs with notes
const getAllWatchedListings = async (
  accessToken: string | null,
  personId: string
): Promise<WatchedListingEntry[]> => {
  const client = getWatchedListingsClient()
  const axiosOptions = buildAxiosRequestConfig({ accessToken })

  const allWatchedListings = await fetchAllResults({
    apiClient: client,
    query: client.getWatchlist,
    params: {
      personId,
      sortBy: [GetWatchlistSortByEnum.DateExpires],
      sortOrder: [GetWatchlistSortOrderEnum.Asc],
    },
    dataKey: 'watchedListings',
    axiosOptions,
  })

  return allWatchedListings.map((w) => {
    const note = w.note || undefined
    return {
      listingId: w.listingId,
      ...(note ? { note } : {}),
    }
  })
}

// Query hook for getting all watched listings with notes
export const allWatchedListingsQuery = makeOptionallyAuthenticatedQuery<
  [string],
  WatchedListingEntry[]
>('all-watched-listings', getAllWatchedListings)

type Return = {
  status: number
  statusText: string
}

const putWatchedListing = async (
  accessToken: string | null,
  requestParams: WatchedListingsApiCreateOrUpdateWatchedListingRequest
): Promise<Return> => {
  const { status, statusText } =
    await getWatchedListingsClient().createOrUpdateWatchedListing(
      requestParams,
      buildAxiosRequestConfig({ accessToken })
    )

  return { status, statusText }
}

/**
 * saved-search writes a `note` sent in this PUT to the listing-notes store, so
 * a success refreshes the listing-notes reads (e.g. Undo re-PUTting a note).
 * It leaves the Watchlist reads alone: every caller (heart, bulk add, note
 * save, single and bulk Undo) refreshes them once after all its PUTs, so a
 * bulk add of N listings refetches the Watchlist once, not N times. A bulk
 * caller passes `refreshesListingNotes: false` and refreshes the listing-notes
 * reads once itself, for the same reason.
 */
const usePutWatchedListing = ({
  refreshesListingNotes = true,
}: ListingNoteWriteOptions = {}) => {
  const accessToken = useAccessToken()
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (
      params: WatchedListingsApiCreateOrUpdateWatchedListingRequest
    ) => putWatchedListing(accessToken || '', params),
    onSuccess: () => {
      if (refreshesListingNotes) void invalidateListingNotes(queryClient)
    },
  })
}

const deleteWatchedListing = async (
  accessToken: string | null,
  requestParams: WatchedListingsApiDeleteWatchedListingRequest
): Promise<Return> => {
  const { status, statusText } =
    await getWatchedListingsClient().deleteWatchedListing(
      requestParams,
      buildAxiosRequestConfig({ accessToken })
    )

  return { status, statusText }
}

export type DeleteWatchedListingParams = Omit<
  WatchedListingsApiDeleteWatchedListingRequest,
  'keepNote'
> & {
  /**
   * true keeps the buyer's note on the listing (`notes-outlive-watchlist` on);
   * false sends no `keepNote`, so saved-search also clears the note (today).
   * Required so every removal path decides it.
   */
  keepNote: boolean
}

/**
 * saved-search clears the note when a listing leaves the Watchlist without
 * `keepNote`, so a success refreshes the listing-notes reads. Like the PUT, it
 * leaves the Watchlist reads to its callers, and the listing-notes reads too
 * when told to (`refreshesListingNotes: false`).
 */
const useDeleteWatchedListing = ({
  refreshesListingNotes = true,
}: ListingNoteWriteOptions = {}) => {
  const accessToken = useAccessToken()
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ keepNote, ...params }: DeleteWatchedListingParams) =>
      deleteWatchedListing(accessToken || '', {
        ...params,
        ...(keepNote ? { keepNote: true } : {}),
      }),
    onSuccess: () => {
      if (refreshesListingNotes) void invalidateListingNotes(queryClient)
    },
  })
}

export {
  getWatchedStatus,
  getAllWatchedListings,
  usePutWatchedListing,
  useDeleteWatchedListing,
}
